import { FormattedMessage } from 'react-intl';
import { useGetVcsRepositories } from 'stores/rq/vcs-connections';
import type { VcsApi } from '@env0/vcs-service/api';
import { useHasPermission } from 'hooks/use-has-permission';
import { useVcsOAuthPopup } from 'hooks/use-vcs-oauth-popup.hook';
import { useVcsConnectionCreator } from './use-vcs-connection-creator.hook';
import VcsRepositorySelector, { type VcsRepositorySelectProps } from './vcs-repository-selector';
import Button from 'components/common/button';
import Tooltip from 'components/common/tooltip';
import styled from 'types/theme.types';
import withLabel from 'components/common/form-controlled/with-label.hoc';

export interface VcsRepositoryOauthSelectorProps {
  vcsType: VcsApi.VcsType;
  form: VcsRepositorySelectProps['form'];
}

const VcsRepositoryOauthSelector: React.FC<VcsRepositoryOauthSelectorProps> = ({ vcsType, form }) => {
  const { data, isFetching: isFetchingRepositories, refetch } = useGetVcsRepositories(vcsType, 'DeploymentPipeline');
  const { isAuthorized: canEditOrgSettings } = useHasPermission('EDIT_ORGANIZATION_SETTINGS');

  const { handleOAuthSuccess, handleOAuthError, isCreatingConnection, setIsCreatingConnection } =
    useVcsConnectionCreator(vcsType, 'DeploymentPipeline', refetch);

  const { openOAuthPopup } = useVcsOAuthPopup({
    connectionType: 'DeploymentPipeline',
    vcsType,
    onSuccess: handleOAuthSuccess,
    onError: handleOAuthError
  });

  const addButton = (
    <AddConnectionButton
      onClick={() => {
        setIsCreatingConnection(true);
        openOAuthPopup();
      }}
      type="primary"
      size="small"
      isLoading={isCreatingConnection}
      disabled={isCreatingConnection || !canEditOrgSettings}>
      <FormattedMessage id="vcs.add-button" />
    </AddConnectionButton>
  );

  return (
    <VcsRepositorySelector
      form={form}
      data={data}
      loading={isFetchingRepositories}
      customActions={
        !canEditOrgSettings ? (
          <Tooltip titleId="permissions.missing.edit-organization-settings" placement="left">
            <span>{addButton}</span>
          </Tooltip>
        ) : (
          addButton
        )
      }
    />
  );
};

const AddConnectionButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export default withLabel(VcsRepositoryOauthSelector);
